// Typ: FindInscribedFromTangentChordTask
// Podtemat: Kąty i styczne → Kąt wpisany z podanego kąta (styczna–cięciwa)

import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import FractionInput, {
  parseFractionLikeInput as parseFraction,
} from "../../../../components/inputs/FractionInput";
import CircleTangentChordTwoAnglesDiagram from "../../../../components/diagrams/CircleTangentChordTwoAnglesDiagram";

const DEC_TOL = 0.1;

/* ========= helpers ========= */
function preprocessRaw(raw) {
  if (raw == null) return "";
  return String(raw).trim().replace(/\u2212/g, "-").replace(/\s+/g, "").replace(",", ".");
}
function pretty(x) {
  const s = String(x ?? "").trim();
  if (!s) return "";
  return s.replace(/\s+/g, "").replace(".", "{,}");
}
function extractRaw(maybe) {
  if (maybe == null) return "";
  if (typeof maybe === "string" || typeof maybe === "number") return String(maybe);
  if (typeof maybe === "object") {
    if ("raw" in maybe) return String(maybe.raw ?? "");
    if ("value" in maybe) return String(maybe.value ?? "");
    if ("input" in maybe) return String(maybe.input ?? "");
  }
  return "";
}
function normalizeChange(v) {
  return typeof v === "string" ? v : extractRaw(v);
}

/* ========= generator ========= */
export function generateTask() {
  const nice = [15, 18, 20, 24, 30, 36, 40, 45, 50, 54, 60, 72, 75, 80, 90, 96, 100, 108, 120, 135, 144, 150];
  const alpha_t = nice[Math.floor(Math.random() * nice.length)];
  const alpha_w = alpha_t;
  const alpha_sr = 2 * alpha_w;

  return {
    type: "FindInscribedFromTangentChordTask",
    data: { alpha_t, alpha_w, alpha_sr },
  };
}

/* ========= treść ========= */
export function renderTask({ data }) {
  const { alpha_t, alpha_sr } = data;

  return (
    <div style={{ marginTop: 8, marginBottom: 8 }}>
      Kąt między styczną w punkcie <InlineMath math={"A"} /> a cięciwą <InlineMath math={"AB"} /> ma miarę{" "}
      <InlineMath math={`\\alpha_{t} = ${alpha_t}^{\\circ}`} />. Oblicz kąt wpisany{" "}
      <InlineMath math={"\\alpha_{w}"} /> oparty na tym samym łuku <InlineMath math={"AB"} />.
      <div style={{ marginTop: 12, display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
        <div style={{ alignSelf: "stretch", textAlign: "left" }}>
          Wizualizacja (ten sam łuk: kąt wpisany i kąt styczna–cięciwa):
        </div>
        <CircleTangentChordTwoAnglesDiagram
          centralDeg={alpha_sr}
          showCentral={false}
          labelW={""}
          labelT={`${alpha_t}^{\\circ}`}
          size={260}
          circleR={105}
          padding={24}
        />
      </div>
    </div>
  );
}

/* ========= input ========= */
export function renderInput(_task, userAnswer, onChange, disabled) {
  const raw = extractRaw(userAnswer);
  return (
    <FractionInput
      value={raw}
      onChange={(v) => onChange({ raw: normalizeChange(v) })}
      disabled={disabled}
      label="α_w"
      suffixLatex="°"
      placeholder="np. 36"
    />
  );
}

/* ========= check ========= */
export function checkAnswer({ data }, userAnswer) {
  const raw = extractRaw(userAnswer);
  if (raw.trim() === "") return false;

  const val = parseFraction(preprocessRaw(raw));
  if (!Number.isFinite(val)) return false;

  const expected = data.alpha_w;
  return Math.abs(val - expected) <= DEC_TOL;
}

/* ========= answer ========= */
export function renderAnswer({ data }) {
  const { alpha_w } = data;
  return (
    <div style={{ marginTop: 6 }}>
      <InlineMath math={`\\alpha_{w} = ${alpha_w}^{\\circ}`} />
    </div>
  );
}

/* ========= explanation ========= */
export function renderExplanation({ data }) {
  const { alpha_t, alpha_w, alpha_sr } = data;
  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      <div><strong>Krok 1.</strong> Twierdzenie (styczna–cięciwa):</div>
      <BlockMath math={"\\alpha_{t}=\\alpha_{w}"} />

      <div><strong>Krok 2.</strong> Podstawiamy daną miarę:</div>
      <BlockMath math={`\\alpha_{w}=\\alpha_{t}=${alpha_t}^{\\circ}`} />

      <div><strong>Krok 3. (Opcjonalnie)</strong> Zależność z kątem środkowym dla tego samego łuku:</div>
      <BlockMath math={"\\alpha_{w}=\\tfrac{1}{2}\\,\\alpha_{\\text{sr}}"} />
      <BlockMath math={`\\alpha_{\\text{sr}}=2\\,\\alpha_{w}=2\\cdot${alpha_w}^{\\circ}=${alpha_sr}^{\\circ}`} />

      <div style={{ marginTop: 12, display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
        <div style={{ alignSelf: "stretch", textAlign: "left" }}>
          Wizualizacja (tylko kąty: wpisany i styczna–cięciwa):
        </div>
        <CircleTangentChordTwoAnglesDiagram
          centralDeg={alpha_sr}
          showCentral={false}
          labelW={`${alpha_w}^{\\circ}`}
          labelT={`${alpha_t}^{\\circ}`}
          size={260}
          circleR={105}
          padding={24}
        />
      </div>
    </div>
  );
}

/* ========= user answer preview ========= */
export function renderUserAnswer(userAnswer) {
  const raw = extractRaw(userAnswer);
  if (raw.trim() === "") {
    return <i>(brak odpowiedzi)</i>;
  }
  return (
    <div style={{ marginTop: 6 }}>
      <InlineMath math={`\\displaystyle \\alpha_{w} = ${pretty(raw)}^{\\circ}`} />
    </div>
  );
}

export default {
  generateTask,
  renderTask,
  renderInput,
  checkAnswer,
  renderAnswer,
  renderExplanation,
  renderUserAnswer,
};
